/**
 * GauShala API Service - Fixed version with proper microservices integration
 * Handles all API calls for cattle management, transactions, and dashboard data
 * Now integrated with BioGas service for dung_transactions data
 */

import biogasService from '../biogasService';

// Gaushala service runs on port 8086 with /gaushala-service context path
// In development, use relative paths to leverage vite proxy configuration
// In production, use the full service URL from environment variables
const isDevelopment = import.meta.env.DEV;
const API_BASE_URL = isDevelopment ? '/gaushala-service' : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');
const GAUSHALA_SERVICE_URL = isDevelopment ? '/gaushala-service' : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');

// Debug logging
if (isDevelopment) {
  console.log('🔍 GAUSHALA_SERVICE_URL (DEV):', GAUSHALA_SERVICE_URL);
  console.log('🔍 BioGas Service imported for dung_transactions');
}

/**
 * Helper function to convert relative photo/document paths to full URLs
 * Backend stores paths like: /uploads/cattle/{id}/photos/{filename}
 * Backend serves files at: /api/v1/gaushala/cattle/files/{cattleId}/{subDir}/{filename}
 *
 * Transforms: /uploads/cattle/123/photos/abc.jpg
 * To: /gaushala-service/api/v1/gaushala/cattle/files/123/photos/abc.jpg
 */
function getFullPhotoUrl(photoPath: string | null | undefined): string | undefined {
  if (!photoPath) return undefined;

  // If already a full URL, return as-is
  if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
    return photoPath;
  }

  // In development, use the proxy path; in production, use full service URL
  const baseUrl = isDevelopment
    ? '/gaushala-service'
    : (import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service');

  // Transform /uploads/cattle/{id}/{subDir}/{filename} to /api/v1/gaushala/cattle/files/{id}/{subDir}/{filename}
  // Pattern: /uploads/cattle/123/photos/filename.jpg -> /api/v1/gaushala/cattle/files/123/photos/filename.jpg
  const uploadPathPattern = /^\/uploads\/cattle\/(\d+)\/(photos|documents)\/(.+)$/;
  const match = photoPath.match(uploadPathPattern);

  if (match) {
    const [, cattleId, subDir, filename] = match;
    return `${baseUrl}/api/v1/gaushala/cattle/files/${cattleId}/${subDir}/${filename}`;
  }

  // Fallback: just append the path as-is (for legacy or different path formats)
  const normalizedPath = photoPath.startsWith('/') ? photoPath : `/${photoPath}`;
  return `${baseUrl}${normalizedPath}`;
}

// ============================================================================
// HELPER FUNCTION FOR SAFE JSON PARSING
// ============================================================================
/**
 * Safely parse JSON response with fallback for empty/invalid responses
 * Prevents "Unexpected end of JSON input" errors
 */
async function safeParseJson(response: Response): Promise<Record<string, any>> {
  try {
    const text = await response.text();
    if (!text) return { message: response.statusText || 'No response body' };
    return JSON.parse(text);
  } catch (e) {
    console.error('Failed to parse response as JSON:', e);
    return { message: `HTTP ${response.status}: ${response.statusText}` };
  }
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Cattle Management APIs - Updated to match backend CowRegistration entity EXACTLY
export interface Cattle {
  // Core Identity
  id?: number;
  uniqueAnimalId: string;
  name: string;
  gaushalaId: number;

  // Classification
  breedId: number;
  speciesId: number;
  genderId: number;
  colorId: number;

  // Physical Attributes
  dob: string;               // LocalDate as ISO string
  weight?: number;
  height?: number;
  hornStatus?: string;
  disability?: string;
  disabilityStatus?: string;  // Health status: HEALTHY, SICK, UNDER_TREATMENT

  // Identification
  earTagNo?: string;         // ✅ Mapped to backend ear_tag_no
  rfidTagNo?: string;        // ✅ Mapped to backend rfid_tag_no
  microchipNo?: string;      // ✅ Mapped to backend microchip_no

  // Health Management
  vaccinationStatus?: string;
  dewormingSchedule?: string;
  medicalHistory?: string;
  lastHealthCheckupDate?: string;  // LocalDate as ISO string
  vetName?: string;
  vetContact?: string;

  // Milk Production
  milkingStatus?: string;
  lactationNumber?: number;
  milkYieldPerDay?: number;
  lastCalvingDate?: string;   // LocalDate as ISO string
  calvesCount?: number;
  pregnancyStatus?: string;

  // Acquisition
  sourceId?: number;
  dateOfAcquisition?: string; // LocalDate as ISO string
  previousOwner?: string;
  ownershipId?: number;
  dateOfEntry?: string;       // LocalDate as ISO string

  // Housing & Feeding
  shedNumber?: string;
  feedingSchedule?: string;
  feedTypeId?: number;

  // Documents
  photoPath?: string;
  vaccinationDocPath?: string;
  healthCertificatePath?: string;
  purchaseDocPath?: string;

  // Timestamps
  createdAt?: string;         // LocalDateTime as ISO string
  updatedAt?: string;         // LocalDateTime as ISO string

  // Legacy/Computed Fields (for backward compatibility)
  photoUrl?: string;          // Alias for photoPath
  healthStatus?: string;      // Derived from vaccinationStatus or other health fields
  isActive?: boolean;         // Not in backend but used in frontend
  totalDungCollected?: number;  // Not in backend but used in frontend
  lastDungCollection?: number;  // Not in backend but used in frontend
}

// Legacy Cattle interface for IoT service compatibility
export interface CattleLegacy {
  id: string;
  rfidTag: string;
  name: string;
  breed: string;
  age: number;
  weight: number;
  health: 'healthy' | 'sick' | 'recovering' | 'vaccination_due';
  owner: string;
  ownerId: string;
  location: {
    latitude: number;
    longitude: number;
    timestamp: number;
    address?: string;
  };
  totalDungCollected: number;
  lastDungCollection?: number;
  isActive: boolean;
  photoUrl?: string;
  medicalHistory: MedicalRecord[];
  createdAt: number;
  updatedAt: number;
}

export interface MedicalRecord {
  id: string;
  date: number;
  type: 'checkup' | 'vaccination' | 'treatment' | 'surgery';
  description: string;
  veterinarian: string;
  medication?: string;
  nextCheckup?: number;
}

export interface Transaction {
  id: string;
  cattleId: string;
  cattleName: string;
  digestorId: string;
  dungWeight: number;
  dungQuality: number;
  worker: string;
  timestamp: number;
  biogasGenerated: number;
  amount: number;
  status: 'pending' | 'completed' | 'cancelled';
  paymentMethod: 'cash' | 'digital' | 'credit';
  notes?: string;
}

export interface DashboardStats {
  totalCattle: number;
  activeCattle: number;
  sickCattle: number;
  totalDungCollected: number;
  todayDungCollection: number;
  totalBiogasGenerated: number;
  todayBiogasGenerated: number;
  totalRevenue: number;
  pendingTransactions: number;
  averageHealthScore: number;
}

// Unified Dashboard Summary DTO - Single API call to fetch all dashboard data
export interface DashboardSummary {
  metrics: DashboardMetrics;
  cowDungTransactionData: CowDungTransactionData[];
  qualityGradeDistribution: QualityGradeDistribution[];  // NEW: Quality grade pie chart data
  todayCollectionSummary: TodayCollectionSummary;        // NEW: Today's collection data
  shedData: ShedOccupancyData[];
  inventoryData: InventoryStockData[];
  medicineData: MedicineStockData[];

  // New dashboard data
  milkProductionSummary?: MilkProductionSummary;       // Milk production metrics
  milkProductionData?: MilkProductionData[];           // Daily milk production for chart
  cattleStatistics?: CattleStatistics;                 // Cattle health and breed breakdown
  upcomingHealthChecks?: UpcomingHealthCheck[];        // Upcoming vaccinations/checkups
  foodConsumptionSummary?: FoodConsumptionSummary;     // Food consumption metrics
}

export interface DashboardMetrics {
  totalCattle: number;
  activeCattle: number;
  sickCattle: number;
  totalCowDungWeight: number;      // Total cow dung collected (kg)
  totalCowDungValue: number;       // Total value of dung collected (₹)
  averageQualityGrade?: string;    // Most common quality grade (A/B/C/D/Mixed/N/A)
  todayCollectionWeight?: number;  // Today's cow dung collection (kg)
  todayCollectionValue?: number;   // Today's collection value (₹)
  averageHealthScore: number;

  // Shed occupancy metrics (real-time cattle distribution)
  totalShedCapacity?: number;      // Total capacity across all sheds
  currentShedOccupancy?: number;   // Current total occupancy across all sheds
  shedUtilizationPercentage?: number; // Overall shed utilization percentage
}

export interface CowDungTransactionData {
  date: string;
  weightKg: number;
  qualityGrade: string;
  totalAmount: number;
  paymentStatus: string;
  transactionCount: number;
}

export interface QualityGradeDistribution {
  grade: string;       // A, B, C, or D
  count: number;       // Number of transactions with this grade
  percentage: number;  // Percentage of total transactions
}

export interface TodayCollectionSummary {
  totalWeight: number;       // Total weight collected today (kg)
  totalValue: number;        // Total value of today's collection (₹)
  transactionCount: number;  // Number of transactions today
  mostCommonGrade: string;   // Most common quality grade today
}

// New Dashboard Data Types
export interface MilkProductionSummary {
  totalMilkProduced: number;      // Total milk in liters for the period
  averageFatPercentage: number;   // Average fat percentage
  averageSnf: number;             // Average SNF
  totalRecords: number;           // Number of production records
  dailyAverage: number;           // Average daily production
}

export interface MilkProductionData {
  date: string;
  quantity: number;          // Liters
  fatPercentage: number;
  snf: number;
  recordCount: number;
}

export interface CattleStatistics {
  totalCattle: number;
  healthyCattle: number;
  sickCattle: number;
  recoveringCattle: number;
  vaccinationDueCattle: number;
  breedDistribution: BreedDistribution[];
  genderDistribution: GenderDistribution[];
}

export interface BreedDistribution {
  breedName: string;
  count: number;
  percentage: number;
}

export interface GenderDistribution {
  genderName: string;
  count: number;
  percentage: number;
}

export interface UpcomingHealthCheck {
  cattleId: number;
  cattleName: string;
  recordType: string;         // Vaccination, Checkup, Treatment
  scheduledDate: string;
  veterinarian: string;
  daysUntil: number;          // Days until scheduled date
  status: string;             // SCHEDULED, OVERDUE
}

export interface FoodConsumptionSummary {
  totalQuantity: number;          // Total food consumed
  totalRecords: number;           // Number of feeding records
  mostUsedFeedType: string;       // Most commonly used feed type
  feedTypeDistribution: FeedTypeDistribution[];
}

export interface FeedTypeDistribution {
  feedType: string;
  quantity: number;
  recordCount: number;
  percentage: number;
}

export interface ShedOccupancyData {
  shedName: string;
  shedCode: string;
  capacity: number;
  occupancy: number;
  occupancyPercentage: number;
  status: string;
}

export interface InventoryStockData {
  category: string;
  itemCount: number;
  totalValue: number;
  lowStockItems: number;
  status: string;
}

export interface MedicineStockData {
  medicineName: string;
  currentStock: number;
  minimumStock: number;
  expiryDate: string;
  daysToExpiry: number;
  status: string;
}

export interface RecentCollection {
  id: string;
  cattleName: string;
  weight: number;
  quality: number;
  worker: string;
  timestamp: number;
  digestorId: string;
  status: 'completed' | 'processing' | 'failed';
}

export interface TopPerformer {
  name: string;
  collections: number;
  totalWeight: number;
  avgQuality: number;
}

// Medicine entity interface - matches backend Medicine.java exactly
export interface Medicine {
  id?: number;
  gaushalaId?: number; // Required for creation (backend fallback)
  name: string;
  description?: string;
  dosage: string;
  unit: string;
  quantity: number;
  expiryDate: string; // LocalDateTime as ISO string
  manufacturer?: string;
  batchNumber?: string;
  purpose?: string;
  createdAt?: string;
  updatedAt?: string;
}

// FoodHistory entity interface - matches backend FoodHistoryDTO.java exactly
export interface FoodHistory {
  id?: number;
  gaushalaId: number;                // Required - links to gaushala
  livestockId: number;
  shedId: number;
  inventoryId?: number;
  consumeQuantity: number;
  duration: string;
  date: string; // LocalDateTime as ISO string
  comments?: string;
  createdAt?: string;
  updatedAt?: string;

  // Nested DTOs for displaying names instead of just IDs
  gaushala?: GaushalaSummaryDTO;     // Shows gaushala name, registration number
  livestock?: LivestockSummaryDTO;   // Shows cattle name, ID, breed
  shed?: ShedSummaryDTO;             // Shows shed number, name
  inventory?: InventorySummaryDTO;   // Shows feed item name, type, unit
}

// Nested summary DTO interfaces
export interface GaushalaSummaryDTO {
  id: number;
  gaushalaName: string;          // e.g., "Shri Krishna Gaushala"
  registrationNumber?: string;   // e.g., "GS-2024-001"
}

export interface LivestockSummaryDTO {
  id: number;
  uniqueAnimalId: string;  // e.g., "COW0001"
  name: string;             // e.g., "Gauri"
  breedName?: string;       // e.g., "Gir"
}

export interface ShedSummaryDTO {
  id: number;
  shedNumber: string;    // e.g., "SHED-A1"
  shedName: string;      // e.g., "Main Cattle Shed"
}

export interface InventorySummaryDTO {
  id: number;
  itemName: string;      // e.g., "Green Fodder"
  typeName?: string;     // e.g., "Feed"
  unitName?: string;     // e.g., "kg"
}

// Paged response interface for list endpoints
export interface PagedResponse<T> {
  content: T[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
}

// Master Data interfaces - for dropdowns and lookups
export interface Breed {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Species {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Gender {
  id: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Color {
  id: number;
  name: string;
  hexCode?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Location {
  id: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  contactPerson?: string;
  contactPhone?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Source {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Ownership {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FeedType {
  id: number;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Master data cache - in-memory caching to avoid repeated API calls
const masterDataCache = {
  breeds: null as Breed[] | null,
  species: null as Species[] | null,
  genders: null as Gender[] | null,
  colors: null as Color[] | null,
  locations: null as Location[] | null,
  sources: null as Source[] | null,
  ownership: null as Ownership[] | null,
  feedTypes: null as FeedType[] | null,
  cacheTimestamp: {
    breeds: 0,
    species: 0,
    genders: 0,
    colors: 0,
    locations: 0,
    sources: 0,
    ownership: 0,
    feedTypes: 0,
  },
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes cache
};

// Helper functions for age/dob conversion
/**
 * Convert age in years to date of birth in ISO format
 * @param ageYears - Age in years
 * @returns ISO formatted date string for dob
 */
export function calculateDobFromAge(ageYears: number): string {
  const today = new Date();
  const birthYear = today.getFullYear() - ageYears;
  const dob = new Date(birthYear, today.getMonth(), today.getDate());
  return dob.toISOString();
}

/**
 * Convert date of birth to age in years
 * @param dob - Date of birth in ISO format
 * @returns Age in years
 */
export function calculateAgeFromDob(dob: string): number {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // Adjust age if birthday hasn't occurred this year yet
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

// API Helper function
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const token = localStorage.getItem('saubhagya_jwt_token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const fullUrl = `${API_BASE_URL}${endpoint}`;
    console.log(`🌐 API Call: ${options.method || 'GET'} ${fullUrl}`);

    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });

    console.log(`📊 Response Status: ${response.status} ${response.statusText} for ${options.method || 'GET'} ${fullUrl}`);

    // Handle empty response body (e.g., 204 No Content or error with no body)
    const contentType = response.headers.get('content-type');
    let data: any = null;

    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          console.error('Failed to parse JSON response:', parseError, 'Text:', text);
          return {
            success: false,
            error: `Invalid JSON response from server: ${parseError instanceof Error ? parseError.message : 'Unknown parse error'}`,
          };
        }
      } else {
        // Empty response body
        data = null;
      }
    } else {
      // Non-JSON response type
      const text = await response.text();
      if (text) {
        data = text;
      } else {
        data = null;
      }
    }

    if (!response.ok) {
      if (data && typeof data === 'object' && data.message) {
        return {
          success: false,
          error: data.message,
        };
      } else if (typeof data === 'string') {
        return {
          success: false,
          error: data || `HTTP error! status: ${response.status}`,
        };
      } else {
        return {
          success: false,
          error: `HTTP error! status: ${response.status}`,
        };
      }
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API call failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// Dashboard API
export const dashboardApi = {
  async getStats(): Promise<ApiResponse<DashboardStats>> {
    try {
      // Call the actual profile endpoint to get real cattle count
      const token = localStorage.getItem('saubhagya_jwt_token');
      const profileResponse = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Failed to fetch profile: ${profileResponse.status}`);
      }

      const profileData = await profileResponse.json();
      const data = profileData.data || profileData;

      // Map profile data to DashboardStats
      const totalCattle = data.statistics?.cattle?.totalCattle || 0;

      // Fetch dung transaction data from biogas service (dung_transactions table)
      let todayDungWeight = 0;
      let monthlyDungWeight = 0;
      let totalRevenue = 0;
      let pendingTransactions = 0;
      let sickCattle = 0;
      let activeCattle = totalCattle;

      try {
        // Fetch dung collections from biogas service (which reads from dung_transactions table)
        const gaushalaId = data.gaushalaId || data.id || data.gaushala_id;
        console.log('📥 Fetching dung transactions from biogas service...');
        console.log('🔑 Gaushala ID:', gaushalaId);
        console.log('📋 Profile data:', data);

        // Use the listDungCollections endpoint without filters - it will get all transactions
        // The backend listDungCollections endpoint is: GET /api/v1/dung-collections?page=0&size=1000
        console.log('🔄 About to fetch dung collections from biogas service...');
        const dungCollectionsResponse = await biogasService.listDungCollections(
          undefined,  // clusterId - optional
          undefined,  // gaushalaId - optional (note: may be gaushalaId not working as filter)
          undefined,  // paymentStatus - optional
          undefined,  // startDate - optional
          undefined,  // endDate - optional
          0,          // page
          1000        // size
        );

        console.log('📥 BIOGAS SERVICE RESPONSE:');
        console.log('   Success:', dungCollectionsResponse.success);
        console.log('   Error:', dungCollectionsResponse.error);
        console.log('   Response:', dungCollectionsResponse);
        console.log('   Data type:', typeof dungCollectionsResponse.data);
        console.log('   Data:', dungCollectionsResponse.data);

        if (!dungCollectionsResponse.success) {
          console.error('❌ API CALL FAILED:', dungCollectionsResponse.error);
        } else if (!dungCollectionsResponse.data) {
          console.warn('⚠️ API returned success but no data');
        } else {
          console.log('✅ API returned data successfully');
          console.log('   Data keys:', Object.keys(dungCollectionsResponse.data));
          console.log('   Is array?', Array.isArray(dungCollectionsResponse.data));
        }

        if (dungCollectionsResponse.success && dungCollectionsResponse.data) {
          let transactionsArray = [];

          // Handle different response formats
          if (Array.isArray(dungCollectionsResponse.data)) {
            transactionsArray = dungCollectionsResponse.data;
            console.log('✅ Response is a direct array');
          } else if (dungCollectionsResponse.data?.content && Array.isArray(dungCollectionsResponse.data.content)) {
            transactionsArray = dungCollectionsResponse.data.content;
            console.log('✅ Response has .content array');
          } else if (dungCollectionsResponse.data?.data && Array.isArray(dungCollectionsResponse.data.data)) {
            transactionsArray = dungCollectionsResponse.data.data;
            console.log('✅ Response has .data array');
          } else {
            console.error('❌ Could not find array in response');
            console.error('   Response structure:', JSON.stringify(dungCollectionsResponse.data, null, 2));
          }

          console.log('📊 Transactions array length:', transactionsArray.length);
          if (transactionsArray.length > 0) {
            console.log('📊 First transaction sample:', JSON.stringify(transactionsArray[0], null, 2));
          }

          const now = new Date();
          const todayDate = now.toISOString().split('T')[0];
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

          console.log('📊 Processing dung transactions:', transactionsArray.length);
          console.log('📅 Today date filter:', todayDate);
          console.log('📅 Month range:', monthStart.toISOString().split('T')[0], 'to', monthEnd.toISOString().split('T')[0]);

          transactionsArray.forEach((transaction: any, index: number) => {
            console.log(`🔍 Transaction #${index}:`, JSON.stringify(transaction));

            // Use EXACT field names from DungCollectionResponse DTO (camelCase)
            const collectionDate = transaction.collectionDate;  // From DungCollectionResponse.collectionDate
            const weight = parseFloat(transaction.weightKg) || 0;  // From DungCollectionResponse.weightKg
            const amount = parseFloat(transaction.totalAmount) || 0;  // From DungCollectionResponse.totalAmount

            console.log(`   ├─ collectionDate: ${collectionDate}`);
            console.log(`   ├─ weightKg: ${weight}`);
            console.log(`   ├─ totalAmount: ${amount}`);
            console.log(`   └─ paymentStatus: ${transaction.paymentStatus}`);

            if (!collectionDate) {
              console.warn(`  ⚠️ Transaction #${index} - Missing collectionDate`);
              return;
            }

            const txDate = new Date(collectionDate);
            const txDateStr = txDate.toISOString().split('T')[0];

            console.log(`  ├─ Date: ${txDateStr}, Weight: ${weight}kg, Amount: ₹${amount}`);

            // Only process transactions with valid weight
            if (weight <= 0) {
              console.warn(`  ⚠️ Transaction #${index} has invalid weight: ${weight}`);
              return;
            }

            // Sum today's dung weight
            if (txDateStr === todayDate) {
              todayDungWeight += weight;
              console.log(`  ✅ TODAY MATCH: Added dung: +${weight}kg (Running Total: ${todayDungWeight}kg)`);
            }

            // Sum monthly dung weight
            if (txDate >= monthStart && txDate <= monthEnd) {
              monthlyDungWeight += weight;
              console.log(`  ✅ MONTH MATCH: Added dung: +${weight}kg (Monthly Total: ${monthlyDungWeight}kg)`);
            }

            // Calculate revenue from ALL transactions with valid amount
            if (amount > 0) {
              totalRevenue += amount;
              console.log(`  💰 REVENUE: +₹${amount} (PaymentStatus: ${transaction.payment_status}, Total: ₹${totalRevenue})`);
            }

            // Count pending transactions
            if (transaction.payment_status === 'PENDING' || transaction.payment_status === 'QUEUED') {
              pendingTransactions += 1;
            }
          });

          console.log('='.repeat(60));
          console.log('📈 FINAL CALCULATIONS:');
          console.log('📈 Dung Today:', todayDungWeight, 'kg');
          console.log('📈 Monthly Dung:', monthlyDungWeight, 'kg');
          console.log('💸 Total Revenue:', totalRevenue, 'INR');
          console.log('⏳ Pending Transactions:', pendingTransactions);
          console.log('='.repeat(60));
        } else {
          console.warn('⚠️ No dung transaction data available from biogas service');
          console.warn('📥 Full Response:', dungCollectionsResponse);
          console.warn('📥 Success flag:', dungCollectionsResponse.success);
          console.warn('📥 Data:', dungCollectionsResponse.data);
        }
      } catch (error) {
        console.error('❌ Error fetching dung transaction data:', error);
        console.error('❌ Error details:', error instanceof Error ? error.message : error);
        console.error('❌ Stack:', error instanceof Error ? error.stack : 'N/A');
      }

      // Fetch health records to calculate average health score and sick cattle
      let averageHealthScore = 0;
      try {
        const healthResponse = await healthRecordsApi.getAllHealthRecords(0, 100);
        if (healthResponse.success && healthResponse.data?.content) {
          const recentRecords = healthResponse.data.content.filter((record: HealthRecord) => {
            // Only include checkups from the last 30 days
            const recordDate = new Date(record.recordDate);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return record.recordType === 'CHECKUP' && recordDate >= thirtyDaysAgo && record.status === 'COMPLETED';
          });

          // Count sick cattle (those requiring treatment or surgery)
          // Note: recordType can be 'TREATMENT', 'SURGERY', 'VACCINATION', 'CHECKUP'
          //       status can be 'SCHEDULED', 'COMPLETED', 'CANCELLED'
          const sickRecords = healthResponse.data.content.filter((record: HealthRecord) => {
            const isSick = record.recordType === 'TREATMENT' || record.recordType === 'SURGERY' ||
                          (record.diagnosis && (record.diagnosis.toLowerCase().includes('sick') || record.diagnosis.toLowerCase().includes('disease')));
            return isSick;
          });

          // Count unique sick cattle
          const uniqueSickCattleIds = new Set(sickRecords.map((r: HealthRecord) => r.cattleId));
          sickCattle = Math.min(uniqueSickCattleIds.size, totalCattle);
          console.log('📊 Health Records Analysis:');
          console.log('   Sick cattle count:', sickCattle);
          console.log('   Total cattle:', totalCattle);
          console.log('   Active cattle (before update):', activeCattle);

          // Always update activeCattle based on sick cattle count
          activeCattle = Math.max(totalCattle - sickCattle, 0);
          console.log('   Active cattle (after update):', activeCattle);

          // Calculate average health score based on number of healthy checkups
          if (recentRecords.length > 0) {
            const healthyRecords = recentRecords.filter((r: HealthRecord) =>
              !r.diagnosis || r.diagnosis.toLowerCase().includes('healthy') || r.diagnosis.toLowerCase().includes('normal')
            );
            averageHealthScore = Math.round((healthyRecords.length / recentRecords.length) * 10);
          } else {
            // Default score if no recent checkups
            averageHealthScore = 7;
          }
        } else {
          console.warn('⚠️ No health records available, using default calculation');
          console.log('📊 Default Active Cattle Calculation:');
          console.log('   Total cattle:', totalCattle);
          console.log('   Sick cattle:', sickCattle);
          // Ensure activeCattle is always recalculated even without health records
          activeCattle = Math.max(totalCattle - sickCattle, 0);
          console.log('   Active cattle:', activeCattle);
          averageHealthScore = 7; // Default health score
        }
      } catch (error) {
        console.warn('⚠️ Error calculating health score:', error);
        console.log('📊 Error recovery - Default Active Cattle Calculation:');
        console.log('   Total cattle:', totalCattle);
        console.log('   Sick cattle:', sickCattle);
        // Ensure activeCattle is always recalculated even on error
        activeCattle = Math.max(totalCattle - sickCattle, 0);
        console.log('   Active cattle:', activeCattle);
        averageHealthScore = 7; // Default health score
      }

      // ⚠️ DO NOT USE DUMMY DATA - Only show real data from backend
      // If no transactions exist, values will be 0 (which is correct)
      console.log('✅ Using REAL DATA from dung_transactions:');
      console.log(`   Today's Dung: ${todayDungWeight} kg (real data)`);
      console.log(`   Monthly Dung: ${monthlyDungWeight} kg (real data)`);
      console.log(`   Total Revenue: ₹${totalRevenue} (real data)`);
      console.log(`   Pending Transactions: ${pendingTransactions} (real data)`);

      const dashboardStats: DashboardStats = {
        totalCattle: totalCattle,
        activeCattle: activeCattle,
        sickCattle: sickCattle,
        totalDungCollected: monthlyDungWeight,
        todayDungCollection: todayDungWeight,
        totalBiogasGenerated: 0, // Not available from dung_transactions
        todayBiogasGenerated: 0, // Not available from dung_transactions
        totalRevenue: totalRevenue,
        pendingTransactions: pendingTransactions,
        averageHealthScore: averageHealthScore,
      };

      console.log('✅ Dashboard stats compiled successfully:', dashboardStats);
      console.log('📊 DISPLAY VALUES:');
      console.log(`   Dung Today: ${todayDungWeight} kg`);
      console.log(`   Total Revenue: ₹${totalRevenue}`);
      console.log(`   Monthly Dung: ${monthlyDungWeight} kg`);
      console.log(`   Pending Transactions: ${pendingTransactions}`);

      return {
        success: true,
        data: dashboardStats,
      };
    } catch (error) {
      console.error('❌ Error fetching dashboard stats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch dashboard stats',
      };
    }
  },

  async getRecentCollections(limit: number = 10): Promise<ApiResponse<RecentCollection[]>> {
    return apiCall<RecentCollection[]>(`/api/dashboard/recent-collections?limit=${limit}`);
  },

  async getTopPerformers(limit: number = 5): Promise<ApiResponse<TopPerformer[]>> {
    return apiCall<TopPerformer[]>(`/api/dashboard/top-performers?limit=${limit}`);
  },

  /**
   * Fetch complete dashboard summary with all metrics and chart data
   * Single unified API call replacing multiple individual API calls
   *
   * @param startDate optional start date for data range (ISO string)
   * @param endDate optional end date for data range (ISO string)
   * @returns DashboardSummary containing all dashboard data
   */
  async getSummary(startDate?: string, endDate?: string): Promise<DashboardSummary> {
    try {
      const token = localStorage.getItem('saubhagya_jwt_token');
      const params = new URLSearchParams();

      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }

      const url = params.toString()
        ? `${GAUSHALA_SERVICE_URL}/api/v1/gaushala/dashboard/summary?${params}`
        : `${GAUSHALA_SERVICE_URL}/api/v1/gaushala/dashboard/summary`;

      console.log('📊 API Request:', {
        url: url,
        method: 'GET',
        hasToken: !!token,
        startDate: startDate,
        endDate: endDate
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      console.log('📨 API Response:', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`Failed to fetch dashboard summary: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('📦 Raw API Response:', result);

      const summary = result.data || result;
      console.log('✅ Dashboard summary extracted:', summary);

      // Validate data structure
      if (!summary.metrics || !summary.milkProductionData) {
        console.warn('⚠️ Missing expected data fields in response:', {
          hasMetrics: !!summary.metrics,
          hasMilkData: !!summary.milkProductionData,
          hasShedData: !!summary.shedData,
          hasInventoryData: !!summary.inventoryData,
          hasMedicineData: !!summary.medicineData
        });
      }

      return summary;

    } catch (error) {
      console.error('❌ Error fetching dashboard summary:', {
        error: error,
        message: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  },
};

// Cattle Management API - Using gaushala-service directly
export const cattleApi = {
  async getAllCattle(page: number = 0, size: number = 20, sortBy: string = 'createdAt'): Promise<ApiResponse<PagedResponse<Cattle>>> {
    // Use Gaushala Service directly on port 8086 with pagination support
    // Add timestamp to prevent browser caching (important after photo uploads)
    const timestamp = new Date().getTime();
    const response = await apiCall<PagedResponse<Cattle>>(`/api/v1/gaushala/cattle?page=${page}&size=${size}&sortBy=${sortBy}&_t=${timestamp}`);

    // Map photoPath to photoUrl with full URL for all cattle in the response
    if (response.success && response.data && response.data.content) {
      response.data.content = response.data.content.map(cattle => ({
        ...cattle,
        photoUrl: getFullPhotoUrl(cattle.photoUrl || cattle.photoPath)
      }));
    }

    return response;
  },

  /**
   * Get cattle by ID from Gaushala Service
   * @param id - Cattle ID (numeric)
   * @returns Cattle details
   */
  async getCattleById(id: number): Promise<ApiResponse<Cattle>> {
    // Add timestamp to prevent browser caching (important after photo uploads)
    const timestamp = new Date().getTime();
    const response = await apiCall<Cattle>(`/api/v1/gaushala/cattle/${id}?_t=${timestamp}`);

    // Map photoPath to photoUrl with full URL for frontend compatibility
    if (response.success && response.data) {
      const cattle = response.data;
      cattle.photoUrl = getFullPhotoUrl(cattle.photoUrl || cattle.photoPath);
    }

    return response;
  },

  async getCattleByRfid(rfidTag: string): Promise<ApiResponse<Cattle>> {
    return apiCall<Cattle>(`/api/v1/cattle/rfid/${encodeURIComponent(rfidTag)}`);
  },

  /**
   * Create new cattle in Gaushala Service
   * @param cattle - Cattle data with proper field mappings (breedId, speciesId, dob, etc.)
   * @param files - Optional object containing all document files
   * @returns Created cattle with generated ID
   */
  async createCattle(
    cattle: Omit<Cattle, 'id' | 'createdAt' | 'updatedAt'>,
    photoFile?: File,
    documentFiles?: {
      healthCertificate?: File;
      vaccinationRecord?: File;
      purchaseDocument?: File;
    }
  ): Promise<ApiResponse<Cattle>> {
    try {
      const token = localStorage.getItem('saubhagya_jwt_token');

      // Create FormData to send cattle data as string parameter and optional files
      const formData = new FormData();
      formData.append('cattleDTOStr', JSON.stringify(cattle));

      // Append photo if provided - rename to ensure backend recognizes it
      if (photoFile) {
        console.log('🔵 [CREATE CATTLE] Adding photo to registration:', photoFile.name);
        const photoFileName = photoFile.name.toLowerCase().includes('photo')
          ? photoFile.name
          : `photo_${Date.now()}.${photoFile.name.split('.').pop()}`;
        const renamedFile = new File([photoFile], photoFileName, { type: photoFile.type });
        formData.append('logo', renamedFile);
        console.log('🔵 [CREATE CATTLE] Photo file renamed to:', photoFileName);
      }

      // Append health certificate if provided
      if (documentFiles?.healthCertificate) {
        console.log('🔵 [CREATE CATTLE] Adding health certificate:', documentFiles.healthCertificate.name);
        const ext = documentFiles.healthCertificate.name.split('.').pop();
        const fileName = `health_certificate_${Date.now()}.${ext}`;
        const renamedFile = new File([documentFiles.healthCertificate], fileName, { type: documentFiles.healthCertificate.type });
        formData.append('logo', renamedFile);
      }

      // Append vaccination record if provided
      if (documentFiles?.vaccinationRecord) {
        console.log('🔵 [CREATE CATTLE] Adding vaccination record:', documentFiles.vaccinationRecord.name);
        const ext = documentFiles.vaccinationRecord.name.split('.').pop();
        const fileName = `vaccination_${Date.now()}.${ext}`;
        const renamedFile = new File([documentFiles.vaccinationRecord], fileName, { type: documentFiles.vaccinationRecord.type });
        formData.append('logo', renamedFile);
      }

      // Append purchase document if provided
      if (documentFiles?.purchaseDocument) {
        console.log('🔵 [CREATE CATTLE] Adding purchase document:', documentFiles.purchaseDocument.name);
        const ext = documentFiles.purchaseDocument.name.split('.').pop();
        const fileName = `purchase_${Date.now()}.${ext}`;
        const renamedFile = new File([documentFiles.purchaseDocument], fileName, { type: documentFiles.purchaseDocument.type });
        formData.append('logo', renamedFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/gaushala/cattle`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      console.log('🔵 [CREATE CATTLE] Response Status:', response.status);

      let data: any;
      const responseText = await response.text();

      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('❌ Failed to parse response as JSON:', responseText.substring(0, 200));
        return {
          success: false,
          error: responseText || `HTTP Error ${response.status}: ${response.statusText}`,
        };
      }

      if (!response.ok) {
        let errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;

        if (data) {
          console.error('❌ Backend error response:', data);
          // Try to extract meaningful error message
          if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          } else if (data.errors) {
            // Handle validation errors
            if (Array.isArray(data.errors)) {
              errorMessage = data.errors.map((e: any) => e.message || e).join(', ');
            } else if (typeof data.errors === 'object') {
              errorMessage = Object.entries(data.errors)
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ');
            }
          }
        }

        console.error('❌ Final error message:', errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      }

      console.log('✅ Cattle created successfully:', data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('❌ CREATE CATTLE EXCEPTION:', errorMsg);
      return {
        success: false,
        error: `Exception: ${errorMsg}. Please check browser console for details.`,
      };
    }
  },

  /**
   * Update existing cattle in Gaushala Service
   * @param id - Cattle ID (numeric)
   * @param updates - Partial cattle data to update
   * @returns Updated cattle details
   */
  async updateCattle(id: number, updates: Partial<Cattle>): Promise<ApiResponse<Cattle>> {
    return apiCall<Cattle>(`/api/v1/gaushala/cattle/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete cattle from Gaushala Service
   * @param id - Cattle ID (numeric)
   * @returns Success status
   */
  async deleteCattle(id: number): Promise<ApiResponse<void>> {
    return apiCall<void>(`/api/v1/gaushala/cattle/${id}`, {
      method: 'DELETE',
    });
  },

  async scanRfid(): Promise<ApiResponse<{ rfidTag: string; cattleInfo?: Cattle }>> {
    // Simulate RFID scanning
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          data: {
            rfidTag: `RFID-${Date.now()}`,
          },
        });
      }, 2000);
    });
  },

  /**
   * Check if a unique animal ID already exists
   * @param uniqueAnimalId - The unique animal ID to check
   * @returns Object with exists boolean and message
   */
  async checkUniqueAnimalId(uniqueAnimalId: string): Promise<ApiResponse<{ exists: boolean; message: string }>> {
    return apiCall<{ exists: boolean; message: string }>(
      `/api/v1/gaushala/cattle/check-unique-id/${encodeURIComponent(uniqueAnimalId)}`
    );
  },

  /**
   * Check if an RFID tag already exists
   * @param rfidTagNo - The RFID tag number to check
   * @returns Object with exists boolean and message
   */
  async checkRfidTag(rfidTagNo: string): Promise<ApiResponse<{ exists: boolean; message: string }>> {
    return apiCall<{ exists: boolean; message: string }>(
      `/api/v1/gaushala/cattle/check-rfid/${encodeURIComponent(rfidTagNo)}`
    );
  },

  async updateHealth(cattleId: string, health: Cattle['health'], notes?: string): Promise<ApiResponse<Cattle>> {
    const updateData: Partial<Cattle> = { health };
    if (notes) {
      // Notes can be used for logging or future implementation
      console.log(`Health update notes for ${cattleId}: ${notes}`);
    }
    return this.updateCattle(cattleId, updateData);
  },

  async addMedicalRecord(cattleId: string, record: Omit<MedicalRecord, 'id'>): Promise<ApiResponse<MedicalRecord>> {
    return apiCall<MedicalRecord>(`/api/v1/cattle/${cattleId}/medical-records`, {
      method: 'POST',
      body: JSON.stringify(record),
    });
  },

  async uploadPhoto(cattleId: string, photoFile: File): Promise<ApiResponse<Cattle>> {
    const formData = new FormData();
    formData.append('photo', photoFile);

    try {
      const token = localStorage.getItem('saubhagya_jwt_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/gaushala/cattle/${cattleId}/photo`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.message || `HTTP error! status: ${response.status}`,
        };
      }

      // Backend returns the updated Cattle DTO with photoPath populated
      const cattle = data as Cattle;
      // Map photoPath to photoUrl with full URL for frontend compatibility
      cattle.photoUrl = getFullPhotoUrl(cattle.photoUrl || cattle.photoPath);

      console.log('✅ Photo uploaded successfully:', cattle);
      return {
        success: true,
        data: cattle,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Upload document for cattle
   * @param cattleId - Cattle ID (numeric)
   * @param documentFile - Document file to upload
   * @param documentType - Type of document (health_certificate, vaccination_record, purchase_document, etc.)
   * @returns Upload result with metadata
   */
  async uploadDocument(cattleId: number | string, documentFile: File, documentType?: string): Promise<ApiResponse<any>> {
    const formData = new FormData();

    try {
      const token = localStorage.getItem('saubhagya_jwt_token');
      console.log(`🔵 [UPLOAD DOCUMENT] Uploading for cattle ${cattleId}, type: ${documentType}`);

      // Rename file to ensure backend recognizes it by the documentType
      // This is important because the backend uses filename detection to determine file type
      let fileName = documentFile.name;

      // Map documentType to filename keywords that backend recognizes
      if (documentType) {
        const fileExt = documentFile.name.split('.').pop();
        switch (documentType.toLowerCase()) {
          case 'health_certificate':
          case 'health-certificate':
            fileName = `health_certificate_${Date.now()}.${fileExt}`;
            break;
          case 'vaccination_record':
          case 'vaccination-record':
            fileName = `vaccination_record_${Date.now()}.${fileExt}`;
            break;
          case 'purchase_document':
          case 'purchase-document':
            fileName = `purchase_document_${Date.now()}.${fileExt}`;
            break;
        }
      }

      // Create a new File with the corrected name
      const renamedFile = new File([documentFile], fileName, { type: documentFile.type });
      formData.append('file', renamedFile);
      console.log(`🔵 [UPLOAD DOCUMENT] File renamed to: ${fileName}`);

      // Build URL with document type parameter if provided
      const url = new URL(`${API_BASE_URL}/api/v1/gaushala/cattle/${cattleId}/documents`);
      if (documentType) {
        url.searchParams.append('documentType', documentType);
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      console.log(`🔵 [UPLOAD DOCUMENT] Response status: ${response.status}`);

      let data: any;
      const responseText = await response.text();

      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('❌ Failed to parse response as JSON:', responseText.substring(0, 200));
        return {
          success: false,
          error: responseText || `HTTP Error ${response.status}: ${response.statusText}`,
        };
      }

      if (!response.ok) {
        let errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;

        if (data) {
          console.error('❌ Backend error response:', data);
          if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          }
        }

        console.error('❌ Document upload failed:', errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      }

      console.log('✅ Document uploaded successfully:', data);
      return {
        success: true,
        data: data.data || data,
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('❌ UPLOAD DOCUMENT EXCEPTION:', errorMsg);
      return {
        success: false,
        error: `Exception: ${errorMsg}. Please check browser console for details.`,
      };
    }
  },

  /**
   * Get documents for a cattle record
   * @param cattleId - Cattle ID (numeric)
   * @returns List of file metadata for documents
   */
  async getCattleDocuments(cattleId: number | string): Promise<ApiResponse<any[]>> {
    try {
      const token = localStorage.getItem('saubhagya_jwt_token');
      console.log(`🔵 [GET DOCUMENTS] Fetching documents for cattle ${cattleId}`);

      const response = await fetch(`${API_BASE_URL}/api/v1/gaushala/cattle/${cattleId}/documents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      console.log(`🔵 [GET DOCUMENTS] Response status: ${response.status}`);

      let data: any;
      const responseText = await response.text();

      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('❌ Failed to parse response as JSON:', responseText.substring(0, 200));
        return {
          success: false,
          error: responseText || `HTTP Error ${response.status}: ${response.statusText}`,
          data: [],
        };
      }

      if (!response.ok) {
        let errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;

        if (data) {
          console.error('❌ Backend error response:', data);
          if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          }
        }

        console.error('❌ Get documents failed:', errorMessage);
        return {
          success: false,
          error: errorMessage,
          data: [],
        };
      }

      console.log('✅ Documents fetched successfully:', data);
      return {
        success: true,
        data: data.data || data || [],
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('❌ GET DOCUMENTS EXCEPTION:', errorMsg);
      return {
        success: false,
        error: `Exception: ${errorMsg}. Please check browser console for details.`,
        data: [],
      };
    }
  },

  /**
   * Search ALL cattle across all pages (not paginated)
   * Searches across: name, uniqueAnimalId, rfidTagNo
   * Results are sorted by latest first (createdAt DESC)
   * @param searchTerm - Search query
   * @returns Complete list of all matching cattle
   */
  async searchAllCattle(searchTerm: string): Promise<ApiResponse<Cattle[]>> {
    if (!searchTerm || searchTerm.trim().length === 0) {
      console.warn('⚠️ Search term is empty');
      return {
        success: false,
        error: 'Search term cannot be empty',
        data: [],
      };
    }

    try {
      const timestamp = new Date().getTime();
      const encodedTerm = encodeURIComponent(searchTerm);
      const endpoint = `/api/v1/gaushala/cattle/search/all?searchTerm=${encodedTerm}&_t=${timestamp}`;

      console.log(`🔍 Starting search for: "${searchTerm}"`);
      console.log(`📍 Endpoint: ${endpoint}`);

      // Backend returns plain array, not wrapped in ApiResponse
      const response = await apiCall<Cattle[]>(endpoint);

      console.log('📋 Raw API response:', response);
      console.log('📊 Response type:', typeof response);
      console.log('📊 Is array:', Array.isArray(response));

      // The response is directly an array
      if (Array.isArray(response)) {
        console.log(`✅ Search successful! Found ${response.length} matching cattle`);
        console.log('Sample cattle:', response.slice(0, 2));

        // Map photoPath to photoUrl with full URL for all cattle in the response
        const cattle = response.map((c: any) => ({
          ...c,
          photoUrl: getFullPhotoUrl(c.photoUrl || c.photoPath)
        }));

        return {
          success: true,
          data: cattle,
        };
      } else {
        // Fallback: if response is wrapped in object
        let cattleArray: any[] = [];
        if (response && typeof response === 'object') {
          if (Array.isArray(response.data)) {
            cattleArray = response.data;
          } else if (Array.isArray(response)) {
            cattleArray = response;
          }
        }

        if (!Array.isArray(cattleArray) || cattleArray.length === 0) {
          console.warn('⚠️ No cattle found matching search term');
          return {
            success: true,
            data: [],
            message: 'No cattle found',
          };
        }

        const cattle = cattleArray.map((c: any) => ({
          ...c,
          photoUrl: getFullPhotoUrl(c.photoUrl || c.photoPath)
        }));

        console.log(`✅ Search successful! Found ${cattle.length} matching cattle`);
        console.log('Sample cattle:', cattle.slice(0, 2));

        return {
          success: true,
          data: cattle,
        };
      }
    } catch (error) {
      console.error('❌ Error during search:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to search cattle',
        data: [],
      };
    }
  },
};

// Transaction API
export const transactionApi = {
  async getAllTransactions(): Promise<ApiResponse<Transaction[]>> {
    return apiCall<Transaction[]>('/api/v1/transactions');
  },

  async getTransactionById(id: string): Promise<ApiResponse<Transaction>> {
    return apiCall<Transaction>(`/api/v1/transactions/${id}`);
  },

  async createTransaction(transaction: Omit<Transaction, 'id' | 'timestamp'>): Promise<ApiResponse<Transaction>> {
    try {
      // Use BiogasServiceClient for proper microservices routing
      const { BiogasServiceClient } = await import('../microservices');
      const result = await BiogasServiceClient.recordContribution({
        cattleId: transaction.cattleId,
        dungWeight: transaction.dungWeight,
        dungQuality: transaction.dungQuality,
        workerId: transaction.worker,
        notes: transaction.notes,
        digestorId: transaction.digestorId,
      });

      return {
        success: result.success || true,
        data: result.data || result,
      };
    } catch (error) {
      console.error('Error creating transaction:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async updateTransaction(id: string, updates: Partial<Transaction>): Promise<ApiResponse<Transaction>> {
    return apiCall<Transaction>(`/api/v1/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async processPayment(transactionId: string, paymentMethod: Transaction['paymentMethod']): Promise<ApiResponse<Transaction>> {
    return apiCall<Transaction>(`/api/v1/transactions/${transactionId}/payment`, {
      method: 'POST',
      body: JSON.stringify({ paymentMethod }),
    });
  },

  async getTransactionsByDigestor(digestorId: string): Promise<ApiResponse<Transaction[]>> {
    return apiCall<Transaction[]>(`/api/v1/transactions/digestor/${digestorId}`);
  },

  async getTransactionsByWorker(workerId: string): Promise<ApiResponse<Transaction[]>> {
    return apiCall<Transaction[]>(`/api/v1/transactions/worker/${workerId}`);
  },

  async getTransactionsByCattle(cattleId: string): Promise<ApiResponse<Transaction[]>> {
    return apiCall<Transaction[]>(`/api/v1/transactions/cattle/${cattleId}`);
  },
};

// Medicine API - All operations with exact backend field names
export const medicineApi = {
  async getAllMedicines(page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<Medicine>>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines?page=${page}&size=${size}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      // WORKAROUND: Backend requires gaushalaId in JWT, but it's not there
      // If we get 403 (Forbidden) or 500 (Internal Server Error), return empty result gracefully
      if (!response.ok && (response.status === 403 || response.status === 500)) {
        console.warn('Backend requires gaushalaId in JWT token, which is missing. Returning empty result.');
        return {
          success: true,
          data: {
            content: [],
            pageNumber: page,
            pageSize: size,
            totalElements: 0,
            totalPages: 0,
            last: true,
            first: true,
          },
        };
      }

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all medicines failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async getMedicineById(id: number): Promise<ApiResponse<Medicine>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get medicine by ID failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async searchMedicines(name: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<Medicine>>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines/search?name=${encodeURIComponent(name)}&page=${page}&size=${size}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Search medicines failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async getExpiredMedicines(): Promise<ApiResponse<Medicine[]>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines/expired`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get expired medicines failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async getLowStockMedicines(threshold: number = 10): Promise<ApiResponse<Medicine[]>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines/low-stock?threshold=${threshold}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get low stock medicines failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async createMedicine(medicine: any): Promise<ApiResponse<Medicine>> {
    try {
      // CRITICAL: Backend MedicineDTO validation requires:
      // - gaushalaId: Long (required, not null)
      // - name: String (required, not null, 2-255 chars)
      // - quantity: Long (required, not null, >= 0)
      // Optional fields can be null

      // Clean and validate the medicine data
      const medicineData = {
        gaushalaId: Number(medicine.gaushalaId), // Must be a number - NO FALLBACK, use provided value
        name: medicine.name || '', // Required string
        quantity: Math.floor(Number(medicine.quantity) || 0), // Must be integer
        description: medicine.description || null,
        dosage: medicine.dosage || null,
        unit: medicine.unit || null,
        expiryDate: medicine.expiryDate || null,
        manufacturer: medicine.manufacturer || null,
        batchNumber: medicine.batchNumber || null,
        purpose: medicine.purpose || null
      };

      console.log('🔵 API: [CREATE MEDICINE] Starting request');
      console.log('🔵 Endpoint:', `${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines`);
      console.log('🔵 Cleaned Data:', JSON.stringify(medicineData, null, 2));
      console.log('🔵 Request Body:', JSON.stringify(medicineData));

      const token = localStorage.getItem('saubhagya_jwt_token');
      console.log('🔵 Auth Token Present:', !!token);

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && {
            Authorization: `Bearer ${token}`
          }),
        },
        body: JSON.stringify(medicineData),
      });

      console.log('🔵 Response Status:', response.status, response.statusText);
      console.log('🔵 Response Headers:', {
        'content-type': response.headers.get('content-type'),
      });

      // Read response as text first to debug
      const responseText = await response.text();
      console.log('🔵 Raw Response:', responseText.substring(0, 500)); // First 500 chars

      // Try to parse as JSON
      let data: any;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('❌ Failed to parse response as JSON');
        data = null;
      }

      if (!response.ok) {
        let errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;

        if (data) {
          console.error('❌ Backend error response (JSON):', data);
          // Try to extract meaningful error message
          if (data.message) {
            errorMessage = `${data.message}`;
          } else if (data.error) {
            errorMessage = `${data.error}`;
          } else if (data.errors) {
            // Handle validation errors
            if (Array.isArray(data.errors)) {
              errorMessage = data.errors.map((e: any) => e.message || e).join(', ');
            } else if (typeof data.errors === 'object') {
              errorMessage = Object.entries(data.errors)
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ');
            } else {
              errorMessage = `${data.errors}`;
            }
          }
        } else {
          console.error('❌ Backend error (non-JSON):', responseText);
          // Use the raw response text if available
          if (responseText) {
            errorMessage = responseText.substring(0, 200);
          }
        }

        console.error('❌ Final error message:', errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      }

      // Success case
      console.log('✅ Medicine created successfully:', data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('❌ CREATE MEDICINE EXCEPTION:', errorMsg);
      console.error('❌ Full error:', error);
      return {
        success: false,
        error: `Exception: ${errorMsg}. Please check browser console for details.`,
      };
    }
  },

  async updateMedicine(id: number, medicine: any): Promise<ApiResponse<Medicine>> {
    try {
      // CRITICAL: Backend MedicineDTO validation requires:
      // - gaushalaId: Long (required, not null)
      // - name: String (required, not null, 2-255 chars)
      // - quantity: Long (required, not null, >= 0)

      // Clean and validate the medicine data
      const medicineData = {
        gaushalaId: Number(medicine.gaushalaId), // Must be a number - NO FALLBACK, use provided value
        name: medicine.name || '', // Required string
        quantity: Math.floor(Number(medicine.quantity) || 0), // Must be integer
        description: medicine.description || null,
        dosage: medicine.dosage || null,
        unit: medicine.unit || null,
        expiryDate: medicine.expiryDate || null,
        manufacturer: medicine.manufacturer || null,
        batchNumber: medicine.batchNumber || null,
        purpose: medicine.purpose || null
      };

      console.log('🔵 API: [UPDATE MEDICINE] Starting request for ID:', id);
      console.log('🔵 Endpoint:', `${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines/${id}`);
      console.log('🔵 Cleaned Data:', JSON.stringify(medicineData, null, 2));
      console.log('🔵 Request Body:', JSON.stringify(medicineData));

      const token = localStorage.getItem('saubhagya_jwt_token');
      console.log('🔵 Auth Token Present:', !!token);

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && {
            Authorization: `Bearer ${token}`
          }),
        },
        body: JSON.stringify(medicineData),
      });

      console.log('🔵 Response Status:', response.status, response.statusText);
      console.log('🔵 Response Headers:', {
        'content-type': response.headers.get('content-type'),
      });

      // Read response as text first to debug
      const responseText = await response.text();
      console.log('🔵 Raw Response:', responseText.substring(0, 500)); // First 500 chars

      // Try to parse as JSON
      let data: any;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('❌ Failed to parse response as JSON');
        data = null;
      }

      if (!response.ok) {
        let errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;

        if (data) {
          console.error('❌ Backend error response (JSON):', data);
          // Try to extract meaningful error message
          if (data.message) {
            errorMessage = `${data.message}`;
          } else if (data.error) {
            errorMessage = `${data.error}`;
          } else if (data.errors) {
            // Handle validation errors
            if (Array.isArray(data.errors)) {
              errorMessage = data.errors.map((e: any) => e.message || e).join(', ');
            } else if (typeof data.errors === 'object') {
              errorMessage = Object.entries(data.errors)
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ');
            } else {
              errorMessage = `${data.errors}`;
            }
          }
        } else {
          console.error('❌ Backend error (non-JSON):', responseText);
          // Use the raw response text if available
          if (responseText) {
            errorMessage = responseText.substring(0, 200);
          }
        }

        console.error('❌ Final error message:', errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      }

      // Success case
      console.log('✅ Medicine updated successfully:', data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('❌ UPDATE MEDICINE EXCEPTION:', errorMsg);
      console.error('❌ Full error:', error);
      return {
        success: false,
        error: `Exception: ${errorMsg}. Please check browser console for details.`,
      };
    }
  },

  async deleteMedicine(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/medicines/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      console.error('Delete medicine failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },
};

// FoodHistory API - All operations with exact backend field names
export const foodHistoryApi = {
  async getAllFoodHistory(page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<FoodHistory>>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/food-history?page=${page}&size=${size}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all food history failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async searchFoodHistory(keyword: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<FoodHistory>>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/food-history/search?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Search food history failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async getFoodHistoryById(id: number): Promise<ApiResponse<FoodHistory>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/food-history/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      // Backend wraps response in ApiResponse: { message, data: {...} }
      return {
        success: true,
        data: data.data || data,  // Unwrap ApiResponse to get the actual FoodHistory object
      };
    } catch (error) {
      console.error('Get food history by ID failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async getFoodHistoryByLivestock(livestockId: number, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<FoodHistory>>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/food-history/livestock/${livestockId}?page=${page}&size=${size}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get food history by livestock failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async getFoodHistoryByShed(shedId: number, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<FoodHistory>>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/food-history/shed/${shedId}?page=${page}&size=${size}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get food history by shed failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async getFoodHistoryByGaushala(gaushalaId: number, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<FoodHistory>>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/food-history/gaushala/${gaushalaId}?page=${page}&size=${size}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get food history by gaushala failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async createFoodHistory(foodHistory: Omit<FoodHistory, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<FoodHistory>> {
    try {
      const response = await apiCall<any>('/api/v1/gaushala/food-history', {
        method: 'POST',
        body: JSON.stringify(foodHistory),
      });

      if (response.success && response.data) {
        // apiCall returns { success: true, data: {...} }
        // backend response is wrapped in ApiResponse { message, data: {...} }
        // response.data contains the entire ApiResponse object
        const actualData = response.data.data || response.data;
        return {
          success: true,
          data: actualData,
          message: response.data.message,
        };
      }

      return {
        success: false,
        error: response.error || 'Failed to create food history',
      };
    } catch (error) {
      console.error('Create food history failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async updateFoodHistory(id: number, foodHistory: Partial<FoodHistory>): Promise<ApiResponse<FoodHistory>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/food-history/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
        body: JSON.stringify(foodHistory),
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      const data = await response.json();
      // Backend wraps response in ApiResponse: { message, data: {...} }
      return {
        success: true,
        data: data.data || data,  // Unwrap ApiResponse to get the actual FoodHistory object
      };
    } catch (error) {
      console.error('Update food history failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async deleteFoodHistory(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/food-history/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await safeParseJson(response);
        return {
          success: false,
          error: error.message || error.error || `HTTP ${response.status}`,
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      console.error('Delete food history failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },
};

// Master Data API - with intelligent caching
export const masterDataApi = {
  /**
   * Get all breeds with caching
   */
  async getAllBreeds(forceRefresh: boolean = false): Promise<ApiResponse<Breed[]>> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        !forceRefresh &&
        masterDataCache.breeds &&
        now - masterDataCache.cacheTimestamp.breeds < masterDataCache.CACHE_DURATION
      ) {
        return {
          success: true,
          data: masterDataCache.breeds,
        };
      }

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/master/breeds`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();

      // Update cache
      masterDataCache.breeds = data;
      masterDataCache.cacheTimestamp.breeds = now;

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all breeds failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Get all species with caching
   */
  async getAllSpecies(forceRefresh: boolean = false): Promise<ApiResponse<Species[]>> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        !forceRefresh &&
        masterDataCache.species &&
        now - masterDataCache.cacheTimestamp.species < masterDataCache.CACHE_DURATION
      ) {
        return {
          success: true,
          data: masterDataCache.species,
        };
      }

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/master/species`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();

      // Update cache
      masterDataCache.species = data;
      masterDataCache.cacheTimestamp.species = now;

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all species failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Get all genders with caching
   */
  async getAllGenders(forceRefresh: boolean = false): Promise<ApiResponse<Gender[]>> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        !forceRefresh &&
        masterDataCache.genders &&
        now - masterDataCache.cacheTimestamp.genders < masterDataCache.CACHE_DURATION
      ) {
        return {
          success: true,
          data: masterDataCache.genders,
        };
      }

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/master/genders`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();

      // Update cache
      masterDataCache.genders = data;
      masterDataCache.cacheTimestamp.genders = now;

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all genders failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Get all colors with caching
   */
  async getAllColors(forceRefresh: boolean = false): Promise<ApiResponse<Color[]>> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        !forceRefresh &&
        masterDataCache.colors &&
        now - masterDataCache.cacheTimestamp.colors < masterDataCache.CACHE_DURATION
      ) {
        return {
          success: true,
          data: masterDataCache.colors,
        };
      }

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/master/colors`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();

      // Update cache
      masterDataCache.colors = data;
      masterDataCache.cacheTimestamp.colors = now;

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all colors failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Get all locations/gaushalas with caching
   */
  async getAllLocations(forceRefresh: boolean = false): Promise<ApiResponse<Location[]>> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        !forceRefresh &&
        masterDataCache.locations &&
        now - masterDataCache.cacheTimestamp.locations < masterDataCache.CACHE_DURATION
      ) {
        return {
          success: true,
          data: masterDataCache.locations,
        };
      }

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/master/villages`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();

      // Update cache
      masterDataCache.locations = data;
      masterDataCache.cacheTimestamp.locations = now;

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all locations failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Get all sources with caching
   */
  async getAllSources(forceRefresh: boolean = false): Promise<ApiResponse<Source[]>> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        !forceRefresh &&
        masterDataCache.sources &&
        now - masterDataCache.cacheTimestamp.sources < masterDataCache.CACHE_DURATION
      ) {
        return {
          success: true,
          data: masterDataCache.sources,
        };
      }

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/master/sources`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();

      // Update cache
      masterDataCache.sources = data;
      masterDataCache.cacheTimestamp.sources = now;

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all sources failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Get all ownership types with caching
   */
  async getAllOwnership(forceRefresh: boolean = false): Promise<ApiResponse<Ownership[]>> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        !forceRefresh &&
        masterDataCache.ownership &&
        now - masterDataCache.cacheTimestamp.ownership < masterDataCache.CACHE_DURATION
      ) {
        return {
          success: true,
          data: masterDataCache.ownership,
        };
      }

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/master/ownerships`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();

      // Update cache
      masterDataCache.ownership = data;
      masterDataCache.cacheTimestamp.ownership = now;

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all ownership types failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Get all feed types with caching
   */
  async getAllFeedTypes(forceRefresh: boolean = false): Promise<ApiResponse<FeedType[]>> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        !forceRefresh &&
        masterDataCache.feedTypes &&
        now - masterDataCache.cacheTimestamp.feedTypes < masterDataCache.CACHE_DURATION
      ) {
        return {
          success: true,
          data: masterDataCache.feedTypes,
        };
      }

      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/master/feed-types`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();

      // Update cache
      masterDataCache.feedTypes = data;
      masterDataCache.cacheTimestamp.feedTypes = now;

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('Get all feed types failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  /**
   * Clear all master data cache
   */
  clearCache(): void {
    masterDataCache.breeds = null;
    masterDataCache.species = null;
    masterDataCache.genders = null;
    masterDataCache.colors = null;
    masterDataCache.locations = null;
    masterDataCache.sources = null;
    masterDataCache.ownership = null;
    masterDataCache.feedTypes = null;
    masterDataCache.cacheTimestamp = {
      breeds: 0,
      species: 0,
      genders: 0,
      colors: 0,
      locations: 0,
      sources: 0,
      ownership: 0,
      feedTypes: 0,
    };
  },

  /**
   * Preload all master data - useful for initial app load
   */
  async preloadAll(): Promise<void> {
    await Promise.all([
      masterDataApi.getAllBreeds(),
      masterDataApi.getAllSpecies(),
      masterDataApi.getAllGenders(),
      masterDataApi.getAllColors(),
      masterDataApi.getAllLocations(),
      masterDataApi.getAllSources(),
      masterDataApi.getAllOwnership(),
      masterDataApi.getAllFeedTypes(),
    ]);
  },
};

// Inventory Management Interfaces
export interface Inventory {
  id?: number;
  itemName: string;
  inventoryTypeId: number;
  inventoryUnitId: number;
  quantity: number;
  minimumStockLevel: number; // Backend field name - THIS is what the API returns
  maximumStockLevel?: number; // Backend field name
  unitPrice?: number; // Backend field name
  location?: string; // Backend field name
  status?: string; // Backend field name
  reorderLevel?: number; // Computed/alias field - for backward compatibility (use minimumStockLevel instead)
  description?: string; // Additional field for item description
  supplier?: string;
  gaushalaId: number;
  inventoryTypeName?: string; // Type name for filtering (e.g., "Feed", "Medicine")
  inventoryUnitName?: string; // Unit name for display
  inventoryUnitSymbol?: string; // Unit symbol (e.g., "kg")
  gaushalaName?: string; // Gaushala name for reference
  createdAt?: string;
  updatedAt?: string;
}

export interface InventoryType {
  id: number;
  name: string;  // Backend field name
  description?: string;
}

export interface InventoryUnit {
  id: number;
  unitName: string;
  abbreviation?: string;
}

export interface StockTransaction {
  id?: number;
  inventoryId: number;
  transactionType: 'IN' | 'OUT';
  quantity: number;
  transactionDate: string;
  performedBy?: string;
  notes?: string;
}

// Inventory API
export const inventoryApi = {
  async getAllInventory(page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<Inventory>>> {
    return apiCall<PagedResponse<Inventory>>(`/api/v1/gaushala/inventory?page=${page}&size=${size}`);
  },

  // async getInventoryById(id: number): Promise<ApiResponse<Inventory>> {
  //   return apiCall<Inventory>(`/api/v1/gaushala/inventory/${id}`);
  // },

  async getInventoryById(id: number): Promise<ApiResponse<Inventory>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/inventory/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();
      // Backend wraps response in ApiResponse: { message, data: {...} }
      return {
        success: true,
        data: data.data || data,  // Unwrap if needed
      };
    } catch (error) {
      console.error('Get inventory by ID failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  

  async createInventory(inventory: Omit<Inventory, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Inventory>> {
    return apiCall<Inventory>(`/api/v1/gaushala/inventory`, {
      method: 'POST',
      body: JSON.stringify(inventory),
    });
  },

  async updateInventory(id: number, updates: Partial<Inventory>): Promise<ApiResponse<Inventory>> {
    return apiCall<Inventory>(`/api/v1/gaushala/inventory/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async deleteInventory(id: number): Promise<ApiResponse<void>> {
    return apiCall<void>(`/api/v1/gaushala/inventory/${id}`, {
      method: 'DELETE',
    });
  },

  async searchInventory(query: string): Promise<ApiResponse<PagedResponse<Inventory>>> {
    return apiCall<PagedResponse<Inventory>>(`/api/v1/gaushala/inventory/search?query=${encodeURIComponent(query)}`);
  },

  async getStockHistory(inventoryId: number): Promise<ApiResponse<StockTransaction[]>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/gaushala/inventory/${inventoryId}/stock-history`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          success: false,
          error: error.message || `HTTP error! status: ${response.status}`,
        };
      }

      const data = await response.json();
      console.log('Stock history raw response:', data);

      // Backend wraps response in ApiResponse: { message, data: [...] }
      // Extract the array from nested data property
      let transactions = data.data || data;

      // Ensure we always return an array
      if (!Array.isArray(transactions)) {
        console.warn('Stock history response is not an array:', transactions);
        transactions = [];
      }

      return {
        success: true,
        data: transactions,
      };
    } catch (error) {
      console.error('Get stock history failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async createStockTransaction(transaction: Omit<StockTransaction, 'id'>): Promise<ApiResponse<StockTransaction>> {
    return apiCall<StockTransaction>(`/api/v1/gaushala/inventory/stock-transaction`, {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  },

  async getInventoryTypes(): Promise<ApiResponse<InventoryType[]>> {
    return apiCall<InventoryType[]>(`/api/v1/gaushala/master/inventory-types`);
  },

  async getInventoryUnits(): Promise<ApiResponse<InventoryUnit[]>> {
    return apiCall<InventoryUnit[]>(`/api/v1/gaushala/inventory/units`);
  },
};

// Shed Management Interfaces
export interface Shed {
  id?: number;
  gaushalaId: number;
  shedName: string;
  shedNumber: string;
  capacity: number;
  currentOccupancy: number;
  shedType?: string;
  areaSqFt?: number;
  ventilationType?: string;
  flooringType?: string;
  waterFacility?: boolean;
  feedingFacility?: boolean;
  status: string; // 'ACTIVE' | 'MAINTENANCE'
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShedCapacity {
  shedId: number;
  shedName: string;
  capacity: number;
  currentOccupancy: number;
  availableSpace: number;
  occupancyPercentage: number;
}

// Shed API
export const shedApi = {
  async getAllSheds(page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<Shed>>> {
    return apiCall<PagedResponse<Shed>>(`/api/v1/gaushala/sheds?page=${page}&size=${size}`);
  },

  async getShedById(id: number): Promise<ApiResponse<Shed>> {
    return apiCall<Shed>(`/api/v1/gaushala/sheds/${id}`);
  },

  async createShed(shed: Omit<Shed, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Shed>> {
    return apiCall<Shed>(`/api/v1/gaushala/sheds`, {
      method: 'POST',
      body: JSON.stringify(shed),
    });
  },

  async updateShed(id: number, updates: Partial<Shed>): Promise<ApiResponse<Shed>> {
    return apiCall<Shed>(`/api/v1/gaushala/sheds/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async deleteShed(id: number): Promise<ApiResponse<void>> {
    return apiCall<void>(`/api/v1/gaushala/sheds/${id}`, {
      method: 'DELETE',
    });
  },

  async getShedsByGaushala(gaushalaId: number): Promise<ApiResponse<Shed[]>> {
    return apiCall<Shed[]>(`/api/v1/gaushala/sheds/gaushala/${gaushalaId}`);
  },

  async getShedsByStatus(status: string): Promise<ApiResponse<Shed[]>> {
    return apiCall<Shed[]>(`/api/v1/gaushala/sheds/by-status?status=${status}`);
  },

  async getAvailableCapacity(gaushalaId: number): Promise<ApiResponse<number>> {
    return apiCall<number>(`/api/v1/gaushala/sheds/gaushala/${gaushalaId}/available-capacity`);
  },

  async getAvailableSheds(): Promise<ApiResponse<Shed[]>> {
    return apiCall<Shed[]>(`/api/v1/gaushala/sheds/available-for-cattle`);
  },

  async getCattleByShed(shedNumber: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<Cattle>>> {
    return apiCall<PagedResponse<Cattle>>(`/api/v1/gaushala/cattle/shed/${encodeURIComponent(shedNumber)}?page=${page}&size=${size}`);
  },
};

// ============================================================================
// MILK PRODUCTION API
// ============================================================================

export interface MilkRecord {
  id?: number;
  gaushalaId?: number;
  entryType?: string;
  shedNumber: string;
  shedName?: string;
  milkQuantity: number;
  fatPercentage?: number;
  snf?: number;
  notes?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MilkProductionStats {
  totalQuantity: number;
  averageFatPercentage: number;
  averageSnfPercentage: number;
  recordCount: number;
}

export const milkProductionApi = {
  async getAllMilkRecords(page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<MilkRecord>>> {
    return apiCall<PagedResponse<MilkRecord>>(`/api/v1/gaushala/milk-records?page=${page}&size=${size}`);
  },

  async getMilkRecordById(id: number): Promise<ApiResponse<MilkRecord>> {
    return apiCall<MilkRecord>(`/api/v1/gaushala/milk-records/${id}`);
  },

  async getMilkRecordsByShed(shedNumber: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<MilkRecord>>> {
    return apiCall<PagedResponse<MilkRecord>>(`/api/v1/gaushala/milk-records/shed/${encodeURIComponent(shedNumber)}?page=${page}&size=${size}`);
  },

  async createMilkRecord(data: MilkRecord): Promise<ApiResponse<MilkRecord>> {
    return apiCall<MilkRecord>('/api/v1/gaushala/milk-records', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateMilkRecord(id: number, data: Partial<MilkRecord>): Promise<ApiResponse<MilkRecord>> {
    return apiCall<MilkRecord>(`/api/v1/gaushala/milk-records/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteMilkRecord(id: number): Promise<ApiResponse<void>> {
    return apiCall<void>(`/api/v1/gaushala/milk-records/${id}`, {
      method: 'DELETE',
    });
  },

  async getTotalMilkQuantity(startDate: string, endDate: string): Promise<ApiResponse<number>> {
    return apiCall<number>(`/api/v1/gaushala/milk-records/total-quantity?startDate=${startDate}&endDate=${endDate}`);
  },

  async getTotalMilkQuantityByShed(shedNumber: string, startDate: string, endDate: string): Promise<ApiResponse<number>> {
    return apiCall<number>(`/api/v1/gaushala/milk-records/shed/${encodeURIComponent(shedNumber)}/total-quantity?startDate=${startDate}&endDate=${endDate}`);
  },

  async getMilkRecordsByDateRange(startDate: string, endDate: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<MilkRecord>>> {
    return apiCall<PagedResponse<MilkRecord>>(`/api/v1/gaushala/milk-records/range?startDate=${startDate}&endDate=${endDate}&page=${page}&size=${size}`);
  },

  async getMilkProductionStats(gaushalaId: number, startDate?: string, endDate?: string): Promise<ApiResponse<MilkProductionStats>> {
    let url = `/api/v1/gaushala/milk-records/stats?gaushalaId=${gaushalaId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    return apiCall<MilkProductionStats>(url);
  },
};

// ============================================================================
// HEALTH RECORDS API
// ============================================================================

export interface HealthRecord {
  id?: number;
  cattleId: number;
  gaushalaId: number;
  recordType: string; // 'VACCINATION' | 'TREATMENT' | 'CHECKUP' | 'SURGERY'
  recordDate: string; // LocalDate as ISO string (YYYY-MM-DD)
  veterinarianName?: string;
  veterinarianLicense?: string;
  veterinarianContact?: string;
  diagnosis?: string;
  treatment?: string;
  medications?: string;
  dosageInstructions?: string;
  vaccinationType?: string;
  nextVaccinationDate?: string; // LocalDate as ISO string
  nextCheckupDate?: string; // LocalDate as ISO string
  cost?: number;
  notes?: string;
  performedBy?: string;
  status?: string; // 'SCHEDULED' | 'COMPLETED' | 'CANCELLED'

  // Audit fields
  createdBy?: number;
  updatedBy?: number;
  isActive?: boolean;
  createdAt?: string; // LocalDateTime as ISO string
  updatedAt?: string; // LocalDateTime as ISO string
}

export const healthRecordsApi = {
  async getAllHealthRecords(page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<HealthRecord>>> {
    // Backend returns ApiResponse wrapper: { message, data: [...], totalElements }
    // apiCall wraps this again, so we get: { success: true, data: { message, data, totalElements } }
    const response = await apiCall<any>(`/api/v1/gaushala/health-records?page=${page}&size=${size}`);

    // Transform to match PagedResponse structure expected by frontend
    if (response.success && response.data) {
      // Extract actual records array and metadata from backend's ApiResponse
      const records = Array.isArray(response.data.data)
        ? response.data.data
        : (Array.isArray(response.data) ? response.data : []);

      const totalElements = response.data.totalElements || records.length || 0;
      const totalPages = Math.ceil(totalElements / size);

      return {
        success: true,
        data: {
          content: records,
          pageNumber: page,
          pageSize: size,
          totalElements: totalElements,
          totalPages: totalPages,
          last: page >= totalPages - 1 || totalPages === 0,
          first: page === 0,
        },
      };
    }

    return {
      success: false,
      error: response.error || 'Failed to fetch health records',
    };
  },

  async getHealthRecordById(id: number): Promise<ApiResponse<HealthRecord>> {
    const response = await apiCall<any>(`/api/v1/gaushala/health-records/${id}`);

    // Backend wraps response in ApiResponse: { message, data: {...} }
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
      };
    }

    return response;
  },

  async getHealthRecordsByCattle(cattleId: number): Promise<ApiResponse<HealthRecord[]>> {
    const response = await apiCall<any>(`/api/v1/gaushala/health-records/cattle/${cattleId}`);

    // Backend wraps response in ApiResponse: { message, data: [...] }
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
      };
    }

    return response;
  },

  async createHealthRecord(data: HealthRecord): Promise<ApiResponse<HealthRecord>> {
    try {
      const response = await apiCall<any>('/api/v1/gaushala/health-records', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      if (response.success && response.data) {
        // apiCall returns { success: true, data: {...} }
        // backend response is wrapped in ApiResponse { message, data: {...} }
        // response.data contains the entire ApiResponse object
        const actualData = response.data.data || response.data;
        return {
          success: true,
          data: actualData,
          message: response.data.message,
        };
      }

      return {
        success: false,
        error: response.error || 'Failed to create health record',
      };
    } catch (error) {
      console.error('Create health record failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  },

  async updateHealthRecord(id: number, data: Partial<HealthRecord>): Promise<ApiResponse<HealthRecord>> {
    const response = await apiCall<any>(`/api/v1/gaushala/health-records/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message,
      };
    }

    return response;
  },

  async deleteHealthRecord(id: number): Promise<ApiResponse<void>> {
    const response = await apiCall<any>(`/api/v1/gaushala/health-records/${id}`, {
      method: 'DELETE',
    });

    if (response.success) {
      return {
        success: true,
        message: response.data?.message || 'Health record deleted successfully',
      };
    }

    return response;
  },

  async getPendingVaccinations(): Promise<ApiResponse<HealthRecord[]>> {
    const response = await apiCall<any>('/api/v1/gaushala/health-records/vaccinations/pending');

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
      };
    }

    return response;
  },

  async getUpcomingCheckups(): Promise<ApiResponse<HealthRecord[]>> {
    const response = await apiCall<any>('/api/v1/gaushala/health-records/checkups/upcoming');

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
      };
    }

    return response;
  },

  async getHealthRecordsByDateRange(startDate: string, endDate: string): Promise<ApiResponse<HealthRecord[]>> {
    const response = await apiCall<any>(`/api/v1/gaushala/health-records/range?startDate=${startDate}&endDate=${endDate}`);

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.data || response.data,
      };
    }

    return response;
  },
};

// ============================================================================
// DUNG RECORDS API
// ============================================================================

export interface DungRecord {
  id?: number;
  date: string;
  entryType?: string;
  cowId?: number;
  dungWeight: number;
  usage?: string;
  notes?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const dungRecordsApi = {
  async getAllDungRecords(page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<DungRecord>>> {
    return apiCall<PagedResponse<DungRecord>>(`/api/v1/gaushala/dung-records?page=${page}&size=${size}`);
  },

  async getDungRecordById(id: number): Promise<ApiResponse<DungRecord>> {
    return apiCall<DungRecord>(`/api/v1/gaushala/dung-records/${id}`);
  },

  async getDungRecordsByCow(cowId: number, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<DungRecord>>> {
    return apiCall<PagedResponse<DungRecord>>(`/api/v1/gaushala/dung-records/cow/${cowId}?page=${page}&size=${size}`);
  },

  async createDungRecord(data: Omit<DungRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<DungRecord>> {
    return apiCall<DungRecord>('/api/v1/gaushala/dung-records', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async getTotalDungWeight(startDate: string, endDate: string): Promise<ApiResponse<number>> {
    return apiCall<number>(`/api/v1/gaushala/dung-records/total-weight?startDate=${startDate}&endDate=${endDate}`);
  },
};

// ============================================================================
// RFID SCANS API
// ============================================================================

export interface RFIDScan {
  id?: number;
  tagIdHex: string;
  cattleId?: number;
  gaushalaId: number;
  scanLocation?: string;
  scanTimestamp: string;
  scannerDeviceId?: string;
  signalStrength?: number;
  notes?: string;
  createdAt?: string;
}

export interface RFIDScanStats {
  totalScans: number;
  uniqueTags: number;
  lastScanTime: string;
  averageScansPerDay: number;
}

export const rfidApi = {
  async getAllScans(page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<RFIDScan>>> {
    return apiCall<PagedResponse<RFIDScan>>(`/api/v1/gaushala/rfid/scans?page=${page}&size=${size}`);
  },

  async getScanById(id: number): Promise<ApiResponse<RFIDScan>> {
    return apiCall<RFIDScan>(`/api/v1/gaushala/rfid/scans/${id}`);
  },

  async getScansByTag(tagIdHex: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<RFIDScan>>> {
    return apiCall<PagedResponse<RFIDScan>>(`/api/v1/gaushala/rfid/scans/tag/${tagIdHex}?page=${page}&size=${size}`);
  },

  async getScansByDateRange(startDate: string, endDate: string, page: number = 0, size: number = 20): Promise<ApiResponse<PagedResponse<RFIDScan>>> {
    return apiCall<PagedResponse<RFIDScan>>(`/api/v1/gaushala/rfid/scans/range?startDate=${startDate}&endDate=${endDate}&page=${page}&size=${size}`);
  },

  async getLatestScanByTag(tagIdHex: string): Promise<ApiResponse<RFIDScan>> {
    return apiCall<RFIDScan>(`/api/v1/gaushala/rfid/scans/tag/${tagIdHex}/latest`);
  },

  async getScanCountByTag(tagIdHex: string): Promise<ApiResponse<number>> {
    return apiCall<number>(`/api/v1/gaushala/rfid/scans/tag/${tagIdHex}/count`);
  },

  async getAllUniqueTags(): Promise<ApiResponse<string[]>> {
    return apiCall<string[]>('/api/v1/gaushala/rfid/scans/tags');
  },

  async getScanStats(gaushalaId: number, startDate?: string, endDate?: string): Promise<ApiResponse<RFIDScanStats>> {
    let url = `/api/v1/gaushala/rfid/scans/stats?gaushalaId=${gaushalaId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    return apiCall<RFIDScanStats>(url);
  },

  async createScan(data: RFIDScan): Promise<ApiResponse<RFIDScan>> {
    return apiCall<RFIDScan>('/api/v1/gaushala/rfid/scans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ============================================================================
// GAUSHALA ACCESS MANAGEMENT API
// ============================================================================

export interface UserGaushalaAccess {
  id?: number;
  userId: number;
  gaushalaId: number;
  grantedAt?: string;
  grantedBy?: number;
  // Enriched data
  gaushalaName?: string;
  location?: string;
}

// Cache for gaushalas to prevent repeated API calls
let gaushalaCache: { data: any[] | null; timestamp: number | null } = { data: null, timestamp: null };
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export const gaushalaAccessApi = {
  /**
   * Get all user-gaushala access records
   * GET /api/v1/admin/gaushala-access/all
   */
  async getAllUserGaushalaAccess(): Promise<ApiResponse<UserGaushalaAccess[]>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/admin/gaushala-access/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data.data || data,
        message: data.message
      };
    } catch (error) {
      console.error('Failed to fetch user-gaushala access records:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch access records'
      };
    }
  },

  /**
   * Grant user access to gaushala
   * POST /api/v1/admin/gaushala-access/grant
   */
  async grantGaushalaAccess(userId: number, gaushalaId: number): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${GAUSHALA_SERVICE_URL}/api/v1/admin/gaushala-access/grant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
        body: JSON.stringify({ userId, gaushalaId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: data.message || 'Access granted successfully'
      };
    } catch (error) {
      console.error('Failed to grant gaushala access:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to grant access'
      };
    }
  },

  /**
   * Revoke user access to gaushala
   * DELETE /api/v1/admin/gaushala-access/revoke
   */
  async revokeGaushalaAccess(userId: number, gaushalaId: number): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(
        `${GAUSHALA_SERVICE_URL}/api/v1/admin/gaushala-access/revoke?userId=${userId}&gaushalaId=${gaushalaId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('saubhagya_jwt_token') && {
              Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
            }),
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: data.message || 'Access revoked successfully'
      };
    } catch (error) {
      console.error('Failed to revoke gaushala access:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to revoke access'
      };
    }
  },

  /**
   * Check if user has access to gaushala
   * GET /api/v1/admin/gaushala-access/check
   */
  async checkGaushalaAccess(userId: number, gaushalaId: number): Promise<ApiResponse<{ hasAccess: boolean }>> {
    try {
      const response = await fetch(
        `${GAUSHALA_SERVICE_URL}/api/v1/admin/gaushala-access/check?userId=${userId}&gaushalaId=${gaushalaId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('saubhagya_jwt_token') && {
              Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
            }),
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data,
        message: data.hasAccess ? 'User has access' : 'User does not have access'
      };
    } catch (error) {
      console.error('Failed to check gaushala access:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to check access'
      };
    }
  },

  /**
   * Get all gaushalas
   * GET /api/v1/gaushala/admin/gaushalas
   */
  async getAllGaushalas(forceRefresh: boolean = false): Promise<ApiResponse<any[]>> {
    try {
      // Check if cache is valid and not forcing refresh
      if (!forceRefresh && gaushalaCache.data && gaushalaCache.timestamp) {
        const now = Date.now();
        if (now - gaushalaCache.timestamp < CACHE_DURATION) {
          console.log('✨ [gaushalaAccessApi.getAllGaushalas] Returning cached data');
          return {
            success: true,
            data: gaushalaCache.data,
            message: 'Gaushalas fetched from cache'
          };
        }
      }

      const url = `${GAUSHALA_SERVICE_URL}/api/v1/gaushala/admin/gaushalas`;
      console.log('🔄 [gaushalaAccessApi.getAllGaushalas] Fetching from URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        }
      });

      console.log('📡 [gaushalaAccessApi.getAllGaushalas] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [gaushalaAccessApi.getAllGaushalas] HTTP Error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📦 [gaushalaAccessApi.getAllGaushalas] Raw response:', JSON.stringify(data, null, 2));

      const extractedData = data.gaushalas || data.data || [];
      console.log('✅ [gaushalaAccessApi.getAllGaushalas] Extracted data:', extractedData);
      console.log('✅ [gaushalaAccessApi.getAllGaushalas] Extracted data is array?:', Array.isArray(extractedData));
      console.log('✅ [gaushalaAccessApi.getAllGaushalas] Extracted data length:', extractedData.length);

      // Update cache
      gaushalaCache = {
        data: extractedData,
        timestamp: Date.now()
      };
      console.log('💾 [gaushalaAccessApi.getAllGaushalas] Cache updated');

      return {
        success: true,
        data: extractedData,
        message: data.message
      };
    } catch (error) {
      console.error('❌ [gaushalaAccessApi.getAllGaushalas] Failed to fetch all gaushalas:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch gaushalas'
      };
    }
  },

  /**
   * Get user details from auth-service
   * GET /api/auth/users/{id}
   */
  async getUserById(userId: number): Promise<ApiResponse<any>> {
    try {
      // Use relative path in dev mode for Vite proxy
      const isDev = import.meta.env.DEV;
      const authBase = isDev ? '/auth-service' : (import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:8081/auth-service');
      const AUTH_API_BASE = `${authBase}/api/auth`;
      const response = await fetch(`${AUTH_API_BASE}/users/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error(`Failed to fetch user ${userId}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch user'
      };
    }
  },

  /**
   * Get gaushala details by ID
   * Note: Backend doesn't have individual gaushala endpoint, so we fetch all and filter
   */
  async getGaushalaById(gaushalaId: string | number): Promise<ApiResponse<any>> {
    try {
      // Fetch all gaushalas and find the one with matching ID
      const response = await gaushalaAccessApi.getAllGaushalas();

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch gaushalas');
      }

      const gaushalas = response.data || [];
      const gaushala = gaushalas.find((g: any) => g.id === Number(gaushalaId));

      if (!gaushala) {
        return {
          success: false,
          error: `Gaushala with ID ${gaushalaId} not found`
        };
      }

      return {
        success: true,
        data: gaushala,
        message: 'Gaushala fetched successfully'
      };
    } catch (error) {
      console.error(`Failed to fetch gaushala ${gaushalaId}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch gaushala'
      };
    }
  },

  /**
   * Create a new gaushala
   * POST /api/v1/gaushalas
   */
  async createGaushala(gaushalaData: any): Promise<ApiResponse<any>> {
    try {
      const fetchUrl = `${GAUSHALA_SERVICE_URL}/api/v1/gaushalas`;
      console.log('📤 Creating Gaushala with URL:', fetchUrl);
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('saubhagya_jwt_token') && {
            Authorization: `Bearer ${localStorage.getItem('saubhagya_jwt_token')}`
          }),
        },
        body: JSON.stringify(gaushalaData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Clear cache after creating a new gaushala
      gaushalaCache = { data: null, timestamp: null };
      console.log('🗑️ [gaushalaAccessApi.createGaushala] Cache cleared after creating gaushala');

      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Gaushala created successfully'
      };
    } catch (error) {
      console.error('Failed to create gaushala:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create gaushala'
      };
    }
  },

  /**
   * Clear the gaushala cache (useful when data changes)
   */
  clearCache() {
    gaushalaCache = { data: null, timestamp: null };
    console.log('🗑️ [gaushalaAccessApi.clearCache] Cache manually cleared');
  }
};

// Combined API export
export const api = {
  dashboard: dashboardApi,
  cattle: cattleApi,
  transactions: transactionApi,
  medicine: medicineApi,
  foodHistory: foodHistoryApi,
  inventory: inventoryApi,
  sheds: shedApi,
  milkProduction: milkProductionApi,
  healthRecords: healthRecordsApi,
  rfid: rfidApi,
  masterData: masterDataApi,
  gaushalaAccess: gaushalaAccessApi,
};

// Legacy export for backward compatibility - maintain nested structure
export const gauShalaApi = {
  cattle: cattleApi,
  dashboard: dashboardApi,
  dungRecords: dungRecordsApi,
  transactions: transactionApi,
  medicine: medicineApi,
  foodHistory: foodHistoryApi,
  inventory: inventoryApi,
  sheds: shedApi,
  milkProduction: milkProductionApi,
  healthRecords: healthRecordsApi,
  rfid: rfidApi,
  masterData: masterDataApi,
  gaushalaAccess: gaushalaAccessApi,
};

export default api;